import { FORMAT_ORIGEM, WITHOUT_PORTFOLIO } from '@constants/Text'
import { ASSETS_TYPES } from '@modules/consolidator/constants/Assets'
import {
  referenciaTemCnpj,
  resolveFundIdentifiers,
} from '@modules/consolidator/utils/fund-label/fund-label'
import {
  formatQuantityDisplay,
  hasQuantityValue,
} from '@modules/consolidator/utils/format-quantity/format-quantity'
import { formatAssetTaxa } from '@modules/consolidator/utils/format-asset-taxa/format-asset-taxa'
import { resolveAssetSaldo } from '@modules/consolidator/utils/resolve-asset-saldo/resolve-asset-saldo'

/**
 * Registry declarativo da ficha "Dados gerais" do modal de detalhes do ativo.
 *
 * Cada campo declara `visible(asset, ctx)` e `getValue(asset, ctx)`; campo sem
 * valor não renderiza e grupo sem campos visíveis some — a mesma estrutura
 * serve para qualquer tipo de ativo (RF, ação, fundo, offshore...).
 *
 * ctx é montado pelo shell do modal: { isClient, isRF, isOffshore, isFundo,
 * hasCupom, hasAmortizacao, showISIN, formatData }.
 */

const VALOR_VAZIO = '-'

/** Acima deste comprimento o valor da ficha trunca e ganha tooltip com o texto inteiro. */
export const MAX_CHARS_VALOR_SEM_TOOLTIP = 22

export const TIPOS_COM_TAXA = [
  'Tesouro Direto',
  'Renda Fixa',
  'Operações Estruturadas',
  'Saldo em Conta',
]

export const TIPOS_COM_PRECO_MEDIO = ['Ações', 'FII', 'Ação', ASSETS_TYPES.CRIPTO]

export const TIPO_PERSONALIZADO = 'Personalizado'

export const TIPO_SALDO_EM_CONTA = 'Saldo em Conta'

export const SKELETON_KPI_KEYS = ['saldo', 'rentabilidade']

export const TAXA_INFO =
  'Essa é a taxa de entrada no papel, eventuais compras/resgates em taxas ' +
  'diferentes são consideradas na marcação a mercado do ativo.'

export const assetTipo = (asset) => asset['Tipo'] || asset.tipo || ''

const referenciaBruta = (asset) => asset['CNPJ'] || asset.referencia || ''

const identificadoresDoFundo = (asset) =>
  resolveFundIdentifiers({
    asset: { ...asset, referencia: referenciaBruta(asset) },
  })

const hasTaxa = (asset) => TIPOS_COM_TAXA.includes(assetTipo(asset))

const isPersonalizado = (asset) => assetTipo(asset) === TIPO_PERSONALIZADO

const boolText = (value) => (value ? 'Sim' : 'Não')

export const FICHA_GROUPS = [
  {
    key: 'classificacao',
    title: 'Classificação',
    fields: [
      {
        key: 'tipo',
        label: 'Tipo',
        getValue: (asset) => assetTipo(asset) || VALOR_VAZIO,
      },
      {
        key: 'classe',
        label: 'Classe',
        getValue: (asset, ctx) => {
          if (ctx.isClient) {
            return (
              asset['subclasse'] ||
              asset['CI'] ||
              asset.ci ||
              asset.classe ||
              asset.Classe ||
              VALOR_VAZIO
            )
          }
          return (
            asset['CI'] || asset.ci || asset.classe || asset.Classe || VALOR_VAZIO
          )
        },
      },
      {
        key: 'subclasse',
        label: 'Subclasse',
        visible: (asset, ctx) => !ctx.isClient,
        getValue: (asset) => asset['subclasse'] || VALOR_VAZIO,
      },
      {
        key: 'subtipo_offshore',
        label: 'Subtipo',
        visible: (asset, ctx) => ctx.isOffshore && asset.subtipo1,
        getValue: (asset) => asset.subtipo1,
      },
      {
        key: 'portfolio',
        label: 'Portfólio',
        getValue: (asset) => {
          const nestedData = asset.data || {}
          return (
            asset.nome_portfolio || nestedData.nome_portfolio || WITHOUT_PORTFOLIO
          )
        },
      },
    ],
  },
  {
    key: 'identificacao',
    title: 'Identificação e origem',
    fields: [
      {
        key: 'corretora',
        label: 'Instituição',
        getValue: (asset) =>
          asset['Corretora'] || asset.corretora || VALOR_VAZIO,
      },
      {
        key: 'origem',
        label: 'Origem',
        visible: (asset) => Boolean(asset.origem),
        getValue: (asset) => FORMAT_ORIGEM[asset.origem] || asset.origem,
      },
      {
        key: 'isin_b3',
        label: 'Código B3 (ISIN)',
        labelOffshore: 'ISIN',
        visible: (asset, ctx) => ctx.showISIN,
        getValue: (asset) => asset.isin || VALOR_VAZIO,
      },
      {
        key: 'isin_plain',
        label: 'ISIN',
        visible: (asset, ctx) => Boolean(asset.isin) && !ctx.showISIN,
        getValue: (asset) => asset.isin,
      },
      {
        key: 'ticker',
        label: 'Ticker',
        visible: (asset, ctx) => ctx.isOffshore && asset.referencia,
        getValue: (asset) => asset.referencia,
      },
      {
        key: 'exchange',
        label: 'Exchange',
        visible: (asset, ctx) => ctx.isOffshore && asset.exchange,
        getValue: (asset) => asset.exchange,
      },
      {
        key: 'moeda',
        label: 'Moeda',
        visible: (asset, ctx) => ctx.isOffshore && asset.moeda,
        getValue: (asset) => asset.moeda,
      },
      {
        key: 'referencia',
        label: 'Referência',
        visible: (asset, ctx) =>
          ctx.isFundo &&
          Boolean(referenciaBruta(asset)) &&
          !referenciaTemCnpj(referenciaBruta(asset)),
        getValue: (asset) => referenciaBruta(asset),
      },
      {
        key: 'fundo_cnpj',
        label: 'CNPJ Fundo',
        visible: (asset, ctx) => ctx.isFundo,
        getValue: (asset) =>
          identificadoresDoFundo(asset).cnpj || VALOR_VAZIO,
      },
      {
        key: 'fundo_subclasse',
        label: 'ID Subclasse (Fundo)',
        visible: (asset, ctx) => ctx.isFundo,
        getValue: (asset) =>
          identificadoresDoFundo(asset).subclasse || VALOR_VAZIO,
      },
      {
        key: 'primeira_aplicacao',
        label: 'Primeira aplicação',
        visible: (asset) =>
          Boolean(
            asset.data_aplicacao_inicial_f &&
              asset.data_aplicacao_inicial_f !== VALOR_VAZIO
          ),
        getValue: (asset) => asset.data_aplicacao_inicial_f,
      },
    ],
  },
  {
    key: 'condicoes',
    title: 'Posição e condições',
    fields: [
      {
        key: 'saldo_atual',
        label: 'Saldo atual',
        getValue: (asset) => resolveAssetSaldo({ asset }) || VALOR_VAZIO,
      },
      {
        key: 'quantidade',
        label: 'Quantidade',
        visible: (asset) => assetTipo(asset) !== TIPO_SALDO_EM_CONTA,
        getValue: (asset) =>
          hasQuantityValue(asset.quantidade)
            ? formatQuantityDisplay(asset.quantidade)
            : VALOR_VAZIO,
      },
      {
        key: 'pu',
        label: 'Preço unitário',
        visible: (asset) => assetTipo(asset) !== TIPO_SALDO_EM_CONTA,
        getValue: (asset) => asset.pu || VALOR_VAZIO,
      },
      {
        key: 'preco_medio',
        label: 'Preço médio',
        visible: (asset) => TIPOS_COM_PRECO_MEDIO.includes(assetTipo(asset)),
        getValue: (asset) => asset['preco_medio'] || VALOR_VAZIO,
      },
      {
        key: 'taxa',
        label: 'Taxa',
        info: TAXA_INFO,
        visible: (asset) => hasTaxa(asset) && !isPersonalizado(asset),
        getValue: (asset) =>
          formatAssetTaxa(asset['Taxa'] || asset.taxa) || VALOR_VAZIO,
      },
      {
        key: 'isento',
        label: 'Isento IR',
        visible: (asset) => !hasTaxa(asset) && !isPersonalizado(asset),
        getValue: (asset) => boolText(asset.isento),
      },
      {
        key: 'vencimento',
        label: 'Data de vencimento',
        visible: (asset) => hasTaxa(asset),
        getValue: (asset) =>
          assetTipo(asset) === TIPO_SALDO_EM_CONTA
            ? VALOR_VAZIO
            : asset['data_vencimento_f'] || VALOR_VAZIO,
      },
      {
        key: 'com_cupom',
        label: 'Pagamento de cupom',
        visible: (asset, ctx) => ctx.hasCupom,
        getValue: (asset) => boolText(asset.com_cupom),
      },
      {
        key: 'cupom_inicio',
        label: 'Início do pagamento de cupom',
        visible: (asset, ctx) => ctx.hasCupom && asset.com_cupom,
        getValue: (asset, ctx) => ctx.formatData(asset.cupom_inicio),
      },
      {
        key: 'cupom_periodicidade',
        label: 'Periodicidade do cupom (meses)',
        visible: (asset, ctx) => ctx.hasCupom && asset.com_cupom,
        getValue: (asset) => asset.cupom_periodicidade_em_meses,
      },
      {
        key: 'com_amortizacao',
        label: 'Pagamento de amortização',
        visible: (asset, ctx) => ctx.hasAmortizacao,
        getValue: (asset) => boolText(asset.com_amortizacao),
      },
      {
        key: 'amortizacao_inicio',
        label: 'Início da amortização',
        visible: (asset, ctx) => ctx.hasAmortizacao && asset.com_amortizacao,
        getValue: (asset, ctx) => ctx.formatData(asset.amortizacao_inicio),
      },
      {
        key: 'amortizacao_periodicidade',
        label: 'Periodicidade da amortização (meses)',
        visible: (asset, ctx) => ctx.hasAmortizacao && asset.com_amortizacao,
        getValue: (asset) => asset.amortizacao_periodicidade_em_meses,
      },
      {
        key: 'cotizacao_manual',
        label: 'Cotização manual',
        visible: (asset) => Boolean(asset.cotizacao_manual),
        getValue: (asset) => boolText(asset.cotizacao_manual),
      },
      {
        key: 'modo_cotizacao',
        label: 'Modo de cotização',
        visible: (asset) => Boolean(asset.cotizacao_manual),
        getValue: (asset) =>
          asset.tipo_cotizacao_manual === 'valor' ? 'Valor' : 'Preço Unitário',
      },
    ],
  },
]

/**
 * Resolve o registry para o par (asset, ctx): devolve apenas grupos com pelo
 * menos um campo visível, cada campo já com label e valor resolvidos.
 */
export const resolveFichaGroups = (asset, ctx) =>
  FICHA_GROUPS.map((group) => ({
    key: group.key,
    title: group.title,
    fields: group.fields
      .filter((field) => (field.visible ? field.visible(asset, ctx) : true))
      .map((field) => ({
        key: field.key,
        label:
          field.labelOffshore && ctx.isOffshore
            ? field.labelOffshore
            : field.label,
        info: field.info || null,
        value: field.getValue(asset, ctx),
      }))
      .filter(
        (field) =>
          field.value !== null &&
          field.value !== undefined &&
          field.value !== ''
      ),
  })).filter((group) => group.fields.length > 0)
